import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";
import ladyWithTabletSrc from "../assets/lady-with-tablet.jpeg";

export default function Login() {
  const { login, resendVerification } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const qEmail = searchParams.get("email");
    if (qEmail) setEmail(qEmail);
    if (searchParams.get("registered") === "1") {
      toast.info("We sent a verification link to your email. Please verify before signing in.");
    }
  }, [searchParams]);

  const handleResend = async (targetEmail) => {
    setResending(true);
    try {
      const data = await resendVerification(targetEmail);
      toast.success(data.message || "Verification email sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send verification email.");
    } finally {
      setResending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email, password);
      toast.success("Welcome back");
      if (res.user.role === "super_admin") nav("/superadmin");
      else nav("/app");
    } catch (err) {
      const code = err?.response?.data?.code;
      const detail = err?.response?.data?.detail;
      if (code === "email_not_verified") {
        const targetEmail = err?.response?.data?.email || email;
        const send = window.confirm(
          "Your email is not verified yet.\n\nDo you want us to send a new verification email?"
        );
        if (send) {
          await handleResend(targetEmail);
        } else {
          toast.error(detail || "Email not verified.");
        }
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src={ladyWithTabletSrc} className="w-full h-full object-cover" alt="Boutique" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C2826]/70 to-transparent" />
        <div className="absolute bottom-10 left-10 text-white max-w-md">
          <h2 className="font-display font-bold text-3xl leading-tight">Run your boutique like a pro.</h2>
          <p className="mt-2 opacity-90">From WhatsApp orders to organised inventory in one afternoon.</p>
        </div>
      </div>
      <div className="p-8 md:p-14 flex flex-col justify-center">
        <BrandLogo to="/" size="xl" className="mb-10" testid="login-logo" />
        <h1 className="font-display font-bold text-3xl">Sign in to your store</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>Enter your details below.</p>

        <form onSubmit={submit} className="mt-8 space-y-4 max-w-md">
          <div>
            <label className="text-sm font-medium">Email</label>
            <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 w-full h-12 px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} placeholder="you@store.com" />
          </div>
          <div>
            <label className="text-sm font-medium">Password</label>
            <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 w-full h-12 px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} placeholder="••••••" />
          </div>
          <button data-testid="login-submit-btn" disabled={busy || resending} className="w-full h-12 rounded-md text-white font-semibold tactile-btn" style={{ background: "var(--primary)" }}>
            {busy ? "Signing in..." : resending ? "Sending email..." : "Sign in"}
          </button>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            New here? <Link to="/register" className="font-semibold" style={{ color: "var(--primary)" }} data-testid="login-register-link">Create a store</Link>
          </p>
          <p className="text-xs pt-4" style={{ color: "var(--text-muted)" }}>Super admin? Use <b>admin@vastrakiran.com</b> / <b>admin@123</b></p>
        </form>
      </div>
    </div>
  );
}
